import { Observable, BehaviorSubject, Subject, combineLatest, of, forkJoin, NEVER } from 'rxjs'
import { map, distinctUntilChanged, tap, catchError, startWith, takeUntil, skip, first, debounceTime, delay, filter } from 'rxjs/operators'
import Route from 'route-parser'
import { isEqual, includes, isString, isFunction, isArray, split, map as _map } from '../libraries/lodash'
import { parse } from 'qs'
import { toQueryString } from './url'
import debug from './debug'
import { RESPONSE_BADDATA, RESPONSE_OFFLINE, RESPONSE_PENDING } from '../constants/responses'
import { REQUEST_TIMEOUT, DEFAULT_CACHE_TTL } from '../constants/request'
import { mapResponse } from '../helpers/dataflow'
import { API_HOST } from '../constants/urls'

export class ApiRequest extends Subject {
  constructor (params, mapping, deps) {
    super()

    Object.assign(this, { count: 0, params, Repeater: new Subject(), deps, wasOffline: false, awaitingRetry: false })

    const Response = this.createRequest().pipe(
      tap(r => this.verifyResponse(r)),
      map(mapResponse(mapping)),
      catchError(
        (e) => debug.warn(`Error processing data from ${params.url}: ${e.message}`) || of(RESPONSE_BADDATA)
      )
    )

    combineLatest(
      Response,
      this.deps.Online
    ).pipe(
      distinctUntilChanged(([resA, onlineA], [resB, onlineB]) => {
        if (this.awaitingRetry) return false
        return isEqual(resA, resB) && onlineA === onlineB
      }),
      debounceTime(5)
    ).subscribe(([response, isOnline]) => {
      if (this.wasOffline && isOnline) {
        this.wasOffline = false
        this.awaitingRetry = true
        debug.log(`[Auto-Retry] Connection restored. Retrying request to ${this.params.url}`)
        this.next(RESPONSE_PENDING)
        this.Repeater.next(true)
      } else if (isOnline) {
        this.awaitingRetry = false
        this.next(response)
      } else {
        this.wasOffline = true
      }
    })
  }

  createRequest () {
    const debugTimeLabel = `Server read ${this.params.url}`

    return this.deps.observeRequest(
      this.params,
      this.Repeater.pipe(
        startWith(true),
        tap(() => debug.time(debugTimeLabel)) // start timer
      )
    ).pipe(
      tap(() => debug.timeEnd(debugTimeLabel))
    )
  }

  verifyResponse (response) {
    if (response === RESPONSE_OFFLINE) {
      const retryDelay = Math.min(Math.pow(++this.count, 2) * 100, 30000)
      of(true).pipe(delay(retryDelay)).subscribe(() => this.Repeater.next(true))
      debug.warn(`Request to ${this.params.url} failed. Retry #${this.count} in ${retryDelay / 1000}s`)
    }
  }

  retry () {
    this.Repeater.next(true)
  }

  get output () {
    return this.pipe(
      takeUntil(this.pipe(filter(r => !includes([RESPONSE_PENDING, RESPONSE_OFFLINE], r)), delay(5))), // until content is delivered
      startWith(RESPONSE_PENDING)
    )
  }
}

export class Api extends BehaviorSubject {
  constructor (routes, deps) {
    super({})

    routes = isArray(routes)
      ? Object.assign({}, ...routes.map(r => ({ [r.name]: r.def })))
      : routes

    Object.assign(this, { routes, deps, requests: {} })
  }

  mergeResponseValue = (url, data) => {
    const mergedValue = Object.assign(
      this.value,
      { [url]: data }
    )

    this.next(mergedValue)
  }

  getRequestProps = (name, values, query, queryModifier = {}) => {
    const [definition, mapping, params = {}] = this.routes[name]
    const route = new Route(definition)
    const [path, queryString] = split(route.reverse(values), '?')

    if (path === false) {
      debug.warn(`Incorrect route parameters for ${name} (def: ${definition})`, values)
      return { params: false, mapping }
    }

    const basePath = API_HOST + path
    const defaultQuery = parse(queryString)

    return {
      params: {
        timeout: REQUEST_TIMEOUT,
        ...params,
        url: basePath + toQueryString({ ...defaultQuery, ...query, ...queryModifier }),
        storeUrl: basePath + toQueryString({ ...defaultQuery, ...query })
      },
      mapping
    }
  }

  refresh = (name, values = {}, query = {}, queryModifier = {}) => {
    if (values === '*') {
      // Get the route definition.
      const [definition] = this.routes[name]
      // Look for any existing cached routes that match the definition.
      const matches = _map(
        this.requests,
        (request, storeUrl) => new Route(definition).match(storeUrl)
      ).filter(values => values)
      // Run the refresh function on all of them and combine the results.
      return forkJoin(
        matches.map(values => {
          return this.refreshRoute(name, values, query, queryModifier)
        })
      )
    }
    // Run the refresh function on a single route.
    return this.refreshRoute(name, values, query, queryModifier)
  }

  refreshRoute = (...args) => {
    const { params, mapping } = this.getRequestProps(...args)

    if (params !== false) {
      new ApiRequest(params, mapping, this.deps)
        .output
        .pipe(filter(res => res !== RESPONSE_PENDING))
        .subscribe(data => this.mergeResponseValue(params.storeUrl, data))
    }

    return this.pipe(
      map(x => x[params.storeUrl]),
      skip(1),
      first()
    )
  }

  fetch = (...args) => {
    const { params, mapping } = this.getRequestProps(...args)
    if (params === false) {
      return NEVER
    }

    return new Observable(
      observer => {
        const ttl = params.cacheTtl ?? DEFAULT_CACHE_TTL
        const existing = this.requests[params.storeUrl]
        const isStale = !existing || ttl === 0 || (Date.now() - existing.timestamp > ttl)

        if (isStale) {
          const request = new ApiRequest(params, mapping, this.deps)
          // Register before emitting: mergeResponseValue synchronously notifies subscribers, which may
          // re-enter fetch() for the same url and must see this request as already in flight.
          this.requests[params.storeUrl] = { request, timestamp: Date.now() }
          this.mergeResponseValue(params.storeUrl, RESPONSE_PENDING)
          request.subscribe(
            data => this.mergeResponseValue(params.storeUrl, data)
          )
        }
        this.pipe(
          map(x => x[params.storeUrl]),
          distinctUntilChanged(isEqual)
        ).subscribe(observer)
      }
    )
  }

  observe = (...args) => {
    const { params, mapping } = this.getRequestProps(...args)

    if (params === false) {
      return NEVER
    }

    if (!Object.hasOwn(this.value, params.url)) {
      this.mergeResponseValue(params.url, RESPONSE_PENDING)
    }

    return new Observable(
      observer => {
        const debugTimeLabel = `Server read ${params.url}`
        debug.time(debugTimeLabel)
        const request = this.deps.observeRequest(params)
          .pipe(
            tap(() => debug.timeEnd(debugTimeLabel)),
            map(mapResponse(mapping)),
            catchError(
              (e) => {
                debug.warn(`Error processing data from ${params.url}: ${e.message}`)
                return of(RESPONSE_BADDATA)
              }
            )
          )
        if (args[args.length - 1]) { // memory
          request.subscribe(data => this.mergeResponseValue(params.url, data))
          this.pipe(map(x => x[params.url])).subscribe(observer)
        } else { request.subscribe(observer) }
      }
    )
  }
}

export class RequestDefinition {
  constructor ({ name = null, url = null, transform = null, cacheTtl = null }) {
    if (!isString(name)) throw new Error(`Incorrect request definition name for url "${url}"`)
    if (!isString(url)) throw new Error(`Incorrect request definition url for name "${name}"`)
    if (!isFunction(transform)) throw new Error(`Incorrect request definition transform for name "${name}", url: "${url}"`)
    Object.assign(this, { name, url, transform, cacheTtl })
  }

  get def () {
    return [this.url, this.transform, { cacheTtl: this.cacheTtl }]
  }
}
